'use client';

import type { Database } from '@medizzconnect/db';
import { cn } from '@medizzconnect/shared';
import { colorToGradient } from '@/lib/color-utils';

type MedicalCategoryRow = Database['public']['Tables']['medical_categories']['Row'];

interface CategoryCardProps {
  category: MedicalCategoryRow;
  isActive: boolean;
  hasSubcategories: boolean;
  isExpanded: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * V1-Style Kategorie-Kachel: w-60 (240px), h-[138px], Gradient-Hintergrund aus DB,
 * Chevron für Subcategories, hover-Effekte.
 */
export function CategoryCard({
  category,
  isActive,
  hasSubcategories,
  isExpanded,
  onClick,
}: CategoryCardProps) {
  const bgGradient = colorToGradient(category.color || '#3B82F6');
  const hoverGradient = colorToGradient(category.color || '#2563EB', false);

  return (
    <div className="flex-shrink-0 w-60 relative">
      <div
        data-category-button
        className={cn(
          'h-[138px] px-5 flex items-center justify-center cursor-pointer select-none',
          'transition-all duration-200 text-center font-medium text-sm',
          'hover:-translate-y-0.5 hover:brightness-110 hover:shadow-md',
          isActive && 'ring-2 ring-inset ring-white/40 brightness-95',
        )}
        style={{
          background: bgGradient,
          color: category.icon_color || '#ffffff',
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.background = hoverGradient;
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.background = bgGradient;
        }}
        onClick={onClick}
      >
        <div className="flex items-center gap-1.5">
          <span className="leading-tight line-clamp-2">{category.name}</span>
          {hasSubcategories && (
            <svg
              className={cn(
                'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200',
                isExpanded && 'rotate-180',
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}
